/**
 * Settlement Results Modal
 * Shows a detailed breakdown of settlement results with parlay cards
 */
import { AlertTriangle, Check, CheckCircle, Info, X } from 'lucide-react';
import type { ReactElement } from 'react';
import type { SettlementResult } from '../hooks/useLabDashboard';
import { ParlayCard } from './settlement/ParlayCard';
import { PendingParlaysSection, PendingPredictionsSection } from './settlement/PendingSection';
import { ErrorCard, PredictionCard } from './settlement/PredictionCard';

interface SettlementResultsModalProps {
  result: SettlementResult;
  error: string | null;
  onClose: () => void;
}

export function SettlementResultsModal({
  result,
  error,
  onClose,
}: SettlementResultsModalProps): ReactElement {
  const hasErrors = result.errors && result.errors.length > 0;
  const hasPredictions = (result.predictionsSettled ?? 0) > 0;
  const isSuccess = !error && (result.legsSettled > 0 || hasPredictions);
  const isNoOp =
    !error && result.legsSettled === 0 && result.parlaysSettled === 0 && !hasPredictions;
  const isPartialSuccess = !error && isSuccess && hasErrors;

  // Determine header info
  const getHeaderInfo = () => {
    if (error) {
      return {
        title: 'Settlement Failed',
        description: 'An error occurred during settlement',
        icon: 'error' as const,
      };
    }
    if (isNoOp) {
      return {
        title: 'Nothing to Settle',
        description: result.message || 'All legs and parlays are already settled',
        icon: 'info' as const,
      };
    }
    if (isPartialSuccess) {
      return {
        title: 'Settlement Complete with Errors',
        description: `${result.legsSettled} leg${result.legsSettled !== 1 ? 's' : ''} settled, but some games had issues`,
        icon: 'warning' as const,
      };
    }
    return {
      title: 'Settlement Complete',
      description: result.message || 'Bets have been settled based on game results',
      icon: 'success' as const,
    };
  };

  const { title, description, icon } = getHeaderInfo();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop - no click handler, only close via buttons */}
      <div className="absolute inset-0 bg-black/70" />

      {/* Modal */}
      <div className="relative bg-theme-surface rounded-xl border border-theme-border w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-theme-border shrink-0">
          <div className="flex items-center gap-3">
            <StatusIcon type={icon} />
            <div>
              <h2 className="text-xl font-semibold text-white">{title}</h2>
              <p className="text-sm text-theme-text-secondary">{description}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-theme-text-secondary hover:text-white hover:bg-theme-surface rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Network/Auth Error */}
          {error && (
            <div className="p-4 bg-theme-danger/30 border border-theme-danger/30 rounded-lg">
              <p className="text-red-300">{error}</p>
            </div>
          )}

          {/* Summary Stats */}
          {!error && <SettlementSummary result={result} hasErrors={hasErrors ?? false} />}

          {/* Settled Parlays - Detailed Cards */}
          {result.settledParlays && result.settledParlays.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-theme-text-secondary mb-3">
                Settled Parlays ({result.settledParlays.length})
              </h3>
              <div className="space-y-4">
                {result.settledParlays.map((parlay) => (
                  <ParlayCard key={parlay.id} parlay={parlay} />
                ))}
              </div>
            </div>
          )}

          {/* Settled AI Predictions - Detailed Cards */}
          {result.settledPredictions && result.settledPredictions.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-theme-text-secondary mb-3 flex items-center gap-2">
                <span className="text-purple-400">AI</span> Settled Predictions (
                {result.predictionsSettled ?? 0} picks)
              </h3>
              <div className="space-y-4">
                {result.settledPredictions.map((prediction, idx) => (
                  <PredictionCard key={`${prediction.aiTier}-${idx}`} prediction={prediction} />
                ))}
              </div>
            </div>
          )}

          {/* Pending AI Predictions */}
          {result.pendingPredictions && result.pendingPredictions.length > 0 && (
            <PendingPredictionsSection pendingPredictions={result.pendingPredictions} />
          )}

          {/* Game Errors */}
          {hasErrors && (
            <div>
              <h3 className="text-sm font-medium text-theme-text-secondary mb-3">
                Settlement Errors ({result.errors!.length})
              </h3>
              <div className="space-y-2">
                {result.errors!.map((err, idx) => (
                  <ErrorCard key={idx} error={err} />
                ))}
              </div>
            </div>
          )}

          {/* No-op message */}
          {isNoOp && !error && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-theme-surface flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-theme-text-tertiary" />
              </div>
              <p className="text-theme-text-secondary">All your bets are already up to date.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-theme-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2.5 bg-theme-surface-hover hover:bg-theme-surface-hover text-white font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Local sub-components
// =============================================================================

function StatusIcon({ type }: { type: 'success' | 'warning' | 'error' | 'info' }): ReactElement {
  const configs = {
    success: { bg: 'bg-theme-success/10', color: 'text-theme-success', Icon: Check },
    warning: {
      bg: 'bg-theme-warning/10',
      color: 'text-theme-warning',
      Icon: AlertTriangle,
    },
    error: { bg: 'bg-theme-danger/10', color: 'text-theme-danger', Icon: X },
    info: { bg: 'bg-theme-surface-hover', color: 'text-theme-text-secondary', Icon: Info },
  };

  const { bg, color, Icon } = configs[type];

  return (
    <div className={`w-10 h-10 rounded-full ${bg} flex items-center justify-center`}>
      <Icon className={`w-6 h-6 ${color}`} />
    </div>
  );
}

/**
 * Settlement Summary - Shows detailed breakdown of what was settled
 */
function SettlementSummary({
  result,
  hasErrors,
}: {
  result: SettlementResult;
  hasErrors: boolean;
}): ReactElement {
  // Calculate leg breakdown from settled parlays
  const legStats = {
    won: 0,
    lost: 0,
    voided: 0,
    pending: 0,
  };

  const parlayStats = {
    won: 0,
    lost: 0,
    voided: 0,
    push: 0,
  };

  // Collect all settled legs for quick reference
  const settledLegs: Array<{
    player: string;
    team: string | null;
    prop: string;
    line: string | null;
    hit: boolean | null;
    actualValue: string | null;
    dnp: boolean;
  }> = [];

  if (result.settledParlays) {
    for (const parlay of result.settledParlays) {
      // Count parlay results
      if (parlay.result === 'won') parlayStats.won++;
      else if (parlay.result === 'lost') parlayStats.lost++;
      else if (parlay.result === 'void') parlayStats.voided++;
      else if (parlay.result === 'push') parlayStats.push++;

      // Count leg results
      for (const leg of parlay.legs) {
        if (leg.dnp) {
          legStats.voided++;
        } else if (leg.hit === true) {
          legStats.won++;
        } else if (leg.hit === false) {
          legStats.lost++;
        } else {
          legStats.pending++;
        }

        settledLegs.push({
          player: leg.player,
          team: leg.team,
          prop: leg.prop,
          line: leg.line,
          hit: leg.hit,
          actualValue: leg.actualValue,
          dnp: leg.dnp,
        });
      }
    }
  }

  const totalLegs = legStats.won + legStats.lost + legStats.voided;
  const hitRate =
    totalLegs > 0 && totalLegs - legStats.voided > 0
      ? ((legStats.won / (totalLegs - legStats.voided)) * 100).toFixed(0)
      : '-';

  return (
    <div className="space-y-4">
      {/* Main Stats Row */}
      <div className="grid grid-cols-2 gap-3">
        {/* Legs Breakdown */}
        <div className="bg-theme-surface rounded-lg p-4 border border-theme-border">
          <div className="text-xs text-theme-text-secondary mb-2">Legs Settled</div>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-2xl font-bold text-white">{totalLegs}</span>
            {result.legsProcessed > totalLegs && (
              <span className="text-sm text-theme-text-tertiary">
                of {result.legsProcessed} processed
              </span>
            )}
          </div>
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-theme-success" />
              <span className="text-theme-success">{legStats.won} won</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-theme-danger" />
              <span className="text-theme-danger">{legStats.lost} lost</span>
            </div>
            {legStats.voided > 0 && (
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-theme-surface-hover" />
                <span className="text-theme-text-secondary">{legStats.voided} voided</span>
              </div>
            )}
          </div>
          {hitRate !== '-' && (
            <div className="mt-2 text-xs text-theme-text-tertiary">
              Hit rate: <span className="text-white font-medium">{hitRate}%</span>
            </div>
          )}
        </div>

        {/* Parlays Breakdown */}
        <div className="bg-theme-surface rounded-lg p-4 border border-theme-border">
          <div className="text-xs text-theme-text-secondary mb-2">Parlays Settled</div>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-2xl font-bold text-white">{result.parlaysSettled}</span>
            {result.parlaysUpdated > result.parlaysSettled && (
              <span className="text-sm text-theme-text-tertiary">
                of {result.parlaysUpdated} updated
              </span>
            )}
          </div>
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-theme-success" />
              <span className="text-theme-success">{parlayStats.won} won</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-theme-danger" />
              <span className="text-theme-danger">{parlayStats.lost} lost</span>
            </div>
            {parlayStats.voided > 0 && (
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-theme-surface-hover" />
                <span className="text-theme-text-secondary">{parlayStats.voided} void</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Additional Info Row */}
      <div className="flex flex-wrap gap-3">
        {(result.gamesProcessed ?? 0) > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-900/30 border border-purple-800/50 rounded-lg">
            <span className="text-xs text-theme-text-secondary">Games Checked:</span>
            <span className="text-sm font-medium text-purple-400">{result.gamesProcessed}</span>
          </div>
        )}
        {(result.predictionsSettled ?? 0) > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-900/30 border border-purple-800/50 rounded-lg">
            <span className="text-xs text-theme-text-secondary">AI Predictions:</span>
            <span className="text-sm font-medium text-purple-400">{result.predictionsSettled}</span>
          </div>
        )}
        {hasErrors && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-theme-danger/30 border border-red-800/50 rounded-lg">
            <span className="text-xs text-theme-text-secondary">Errors:</span>
            <span className="text-sm font-medium text-theme-danger">
              {result.errors?.length || 0}
            </span>
          </div>
        )}
        {/* Show count badge if there are pending parlays */}
        {result.pendingParlays && result.pendingParlays.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-theme-primary/30 border border-blue-800/50 rounded-lg">
            <span className="text-xs text-theme-text-secondary">Still Pending:</span>
            <span className="text-sm font-medium text-theme-primary">
              {result.pendingParlays.length} parlay
              {result.pendingParlays.length !== 1 ? 's' : ''}
            </span>
            <span className="text-xs text-blue-300/60">(games not finished)</span>
          </div>
        )}
        {/* Show count badge if there are pending predictions */}
        {result.pendingPredictions && result.pendingPredictions.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-900/30 border border-purple-800/50 rounded-lg">
            <span className="text-xs text-theme-text-secondary">Predictions Pending:</span>
            <span className="text-sm font-medium text-purple-400">
              {result.pendingPredictions.reduce((sum, p) => sum + p.legs.length, 0)} pick
              {result.pendingPredictions.reduce((sum, p) => sum + p.legs.length, 0) !== 1
                ? 's'
                : ''}
            </span>
          </div>
        )}
      </div>

      {/* Pending Parlays - games haven't ended yet */}
      {result.pendingParlays && result.pendingParlays.length > 0 && (
        <PendingParlaysSection pendingParlays={result.pendingParlays} />
      )}

      {/* Quick Player Summary - only if we have settled legs */}
      {settledLegs.length > 0 && settledLegs.length <= 10 && (
        <QuickPlayerSummary settledLegs={settledLegs} />
      )}
    </div>
  );
}

type SettledLegSummary = {
  player: string;
  team: string | null;
  prop: string;
  line: string | null;
  hit: boolean | null;
  actualValue: string | null;
  dnp: boolean;
};

function QuickPlayerSummary({ settledLegs }: { settledLegs: SettledLegSummary[] }): ReactElement {
  return (
    <div className="bg-theme-surface/50 rounded-lg p-3 border border-theme-border/50">
      <div className="text-xs text-theme-text-secondary mb-2">Players Settled</div>
      <div className="flex flex-wrap gap-2">
        {settledLegs.map((leg, idx) => (
          <div
            key={idx}
            className={`px-2 py-1 rounded text-xs flex items-center gap-1.5 ${
              leg.dnp
                ? 'bg-theme-surface-hover text-theme-text-secondary'
                : leg.hit
                  ? 'bg-theme-success/40 text-green-300 border border-green-800/50'
                  : 'bg-theme-danger/40 text-red-300 border border-red-800/50'
            }`}
          >
            <span className="font-medium">{leg.player}</span>
            {leg.team && <span className="text-theme-text-tertiary">({leg.team})</span>}
            <span className="text-theme-text-secondary">·</span>
            <span>{leg.line ? `${leg.prop?.split(' ')[0] || ''} ${leg.line}` : leg.prop}</span>
            {!leg.dnp && leg.actualValue && (
              <>
                <span className="text-theme-text-secondary">→</span>
                <span className="font-medium">{parseFloat(leg.actualValue).toFixed(1)}</span>
              </>
            )}
            {leg.dnp && <span className="text-theme-text-tertiary italic">DNP</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
